import type { IterableContainer } from "./internal/types/IterableContainer";
import { purry } from "./purry";

type Only<T extends IterableContainer> = T extends
  | readonly [...unknown[], unknown, unknown]
  | readonly []
  | readonly [unknown, ...unknown[], unknown]
  | readonly [unknown, unknown, ...unknown[]]
  ? undefined
  : T extends readonly [unknown]
    ? T[number]
    : T[number] | undefined;

/**
 * Returns the first and only element of `array`, or undefined otherwise.
 *
 * @param array - The target array.
 * @signature
 *    R.only(array)
 * @example
 *    R.only([]) // => undefined
 *    R.only([1]) // => 1
 *    R.only([1, 2]) // => undefined
 * @dataFirst
 * @category Array
 */
export function only<T extends IterableContainer>(array: Readonly<T>): Only<T>;

/**
 * Returns the first and only element of `array`, or undefined otherwise.
 *
 * @signature
 *    R.only()(array)
 * @example
 *    R.pipe([], R.only()); // => undefined
 *    R.pipe([1], R.only()); // => 1
 *    R.pipe([1, 2], R.only()); // => undefined
 * @dataLast
 * @category Array
 */
export function only<T extends IterableContainer>(): (
  array: Readonly<T>,
) => Only<T>;

export function only(...args: readonly unknown[]): unknown {
  return purry(onlyImplementation, args);
}

const onlyImplementation = <T>(array: readonly T[]): T | undefined =>
  array.length === 1 ? array[0] : undefined;
